import { Action, createReducer, on } from '@ngrx/store';
import { IFolder, IFile, IEditFile, IDrive } from '@app-root-interfaces';
import * as documentActions from '../actions/document.actions';

export const documentFeatureKey = 'document';

export interface State {
    folders: {
        [key: string]: IFolder;
    };
    files: {
        [key: string]: IFile;
    };
    basicFiles: IFile[];
    basicFolder: IFolder;
    drive: string;
    driveIdCollection: string[];
    path: string[];
    search: IFile[];
    toEdit: IEditFile;
    editLoading: boolean;
    allDrives?: IDrive[];
}

export const initialState: State = {
    folders: {},
    files: {},
    basicFolder: null,
    basicFiles: [],
    path: [],
    search: [],
    drive: null,
    driveIdCollection: null,
    toEdit: null,
    editLoading: false
};

const documentReducer = createReducer(
    initialState,
    on(
        documentActions.addToPathSuccess,
        documentActions.removeFromPathSuccess,
        documentActions.setPathBackSuccess,
        documentActions.buildPathToNodeSuccess,
        (state, { path }) => ({
            ...state,
            path
        })
    ),
    on(documentActions.getBasicSuccess, (state, { folder }) => {
        const newState = {
            ...state,
            basicFiles: folder.files,
            basicFolder: folder,
            files: { ...state.files }
        };
        if (folder.files) {
            folder.files.map((file: IFile) => {
                if (!newState.files[file.id]) {
                    newState.files[file.id] = file;
                }
            });
        }
        return newState;
    }),
    on(documentActions.getDriveSuccess, (state, { drive }) => {
        const newState = {
            ...state,
            folders: {
                ...state.folders,
                [drive.id]: { ...drive, isDrive: true }
            },
            files: { ...state.files },
            path: !state.path || state.path.length === 0 || state.drive !== drive.id ? [drive.id] : state.path,
            drive: drive.id
        };
        drive.folders.map((folder: IFolder) => {
            if (!newState.folders[folder.id]) {
                newState.folders[folder.id] = folder;
            }
        });
        drive.files.map((file: IFile) => {
            if (!newState.files[file.id]) {
                newState.files[file.id] = file;
            }
        });
        return newState;
    }),
    on(documentActions.setDriveCollectionIds, (state, { collectionIds }) => {
        return {
            ...state,
            driveIdCollection: collectionIds
        };
    }),
    on(documentActions.getFavouritesSuccess, (state, { favourites }) => {
        const newState = { ...state, files: { ...state.files } };
        favourites.map((file) => {
            if (!newState.files[file.id]) {
                newState.files[file.id] = file;
            }
        });
        return newState;
    }),
    on(documentActions.getFolderSuccess, (state, { folder }) => {
        const newState = {
            ...state,
            folders: { ...state.folders, [folder.id]: { ...folder, isDrive: false } },
            files: { ...state.files }
        };
        folder.folders.map((child: IFolder) => {
            if (!newState.folders[child.id]) {
                newState.folders[child.id] = child;
            }
        });
        folder.files.map((file: IFile) => {
            if (!newState.files[file.id]) {
                newState.files[file.id] = file;
            }
        });
        return newState;
    }),
    on(documentActions.getRecentlySeenSuccess, (state, { recentlySeen }) => {
        const newState = { ...state, files: { ...state.files } };
        recentlySeen.map((file) => {
            if (!newState.files[file.id]) {
                newState.files[file.id] = file;
            }
        });
        return newState;
    }),
    on(documentActions.searchFilesSuccess, (state, { files }) => {
        const newState: State = {
            ...state,
            files: { ...state.files },
            search: []
        };
        files.map((file: IFile) => {
            if (!newState.search.some((existing) => existing.id === file.id)) {
                newState.search = [...newState.search, file];
            }
        });
        return newState;
    }),
    on(documentActions.searchFilesFailure, (state) => ({
        ...state,
        search: []
    })),
    on(documentActions.addFileToFolders, (state, { file }) => {
        const folders = [];
        if (file.parentFolders && file.parentFolders.length > 0) {
            file.parentFolders.map((fileFolder) => {
                const newFolder: IFolder = {
                    ...state.folders[fileFolder.id],
                    files: state.folders[fileFolder.id].files ? [...state.folders[fileFolder.id].files, file] : [file]
                };
                folders.push(newFolder);
            });
        }
        if (file.parentDrive) {
            const newFolder: IFolder = {
                ...state.folders[file.parentDrive.id],
                files: state.folders[file.parentDrive.id].files
                    ? [...state.folders[file.parentDrive.id].files, file]
                    : [file]
            };
            folders.push(newFolder);
        }

        const newState = {
            ...state,
            files: { ...state.files, [file.id]: file },
            folders: { ...state.folders }
        };

        newState.folders = folders.reduce(
            (previous, newFolder) => ({ ...previous, [newFolder.id]: newFolder }),
            newState.folders
        );
        return newState;
    }),
    on(documentActions.createFolderSuccess, (state, { folder }) => {
        folder.folders = folder.folders ? folder.folders : [];
        const newState = {
            ...state,
            folders: {
                ...state.folders,
                [folder.id]: folder
            }
        };
        newState.folders = folder.folders.reduce(
            (previous, current) => ({ ...previous, [current.id]: current }),
            newState.folders
        );
        const parent = state.folders[folder.parentId];
        newState.folders[parent.id] = {
            ...parent,
            folders: parent.folders ? [...parent.folders, folder] : [folder]
        };
        return newState;
    }),
    on(documentActions.getFileSuccess, (state, { file }) => ({
        ...state,
        files: {
            ...state.files,
            [file.id]: file
        }
    })),
    on(documentActions.deleteFolderSuccess, (state, { folder }) => {
        const folders = { ...state.folders };
        delete folders[folder.id];
        return {
            ...state,
            folders
        };
    }),
    on(documentActions.patchFolderSuccess, (state, { folder }) => ({
        ...state,
        folders: { ...state.folders, [folder.id]: folder }
    })),
    on(documentActions.deleteFileSuccess, (state, { file }) => {
        const files = { ...state.files };
        delete files[file.id];
        return {
            ...state,
            files
        };
    }),
    on(documentActions.startEditFile, documentActions.changeFileSuccess, (state, { file }) => ({
        ...state,
        toEdit: file
    })),
    on(documentActions.cancelEditFile, (state) => ({
        ...state,
        toEdit: null
    })),
    // TODO in effect auslagern
    on(documentActions.updateFileSuccess, (state, { file }) => {
        const originalFile = state.files[file.id];
        // Check if files folders contains basic folder
        const basicFolder = file.parentFolders.filter((folder) => folder.id === state.basicFolder.id);
        // Remove file from Basic files if exists
        let basicFiles = state.basicFiles.filter((basicFile) => basicFile.id !== file.id);
        // if file is in basic folder add it to basic Files
        if (basicFolder.length === 1) {
            basicFiles = [...basicFiles, file];
        }

        let foldersToDelete: IFolder[] = [];
        let foldersToAdd: IFolder[] = [];
        const newState = {
            ...state,
            files: { ...state.files, [file.id]: file },
            folders: { ...state.folders },
            basicFiles: [...basicFiles],
            basicFolder: {
                ...state.basicFolder,
                files: [...basicFiles]
            },
            toEdit: null,
            editLoading: false
        };

        // File zu Drive hinzufügen oder löschen
        if (originalFile.parentDrive && !file.parentDrive) {
            foldersToDelete.push({
                ...state.folders[originalFile.parentDrive.id],
                files: [...state.folders[originalFile.parentDrive.id].files]
            });
        }
        if (!originalFile.parentDrive && file.parentDrive) {
            foldersToAdd.push({
                ...state.folders[file.parentDrive.id],
                files: [...state.folders[file.parentDrive.id].files]
            });
        }
        if (originalFile.parentFolders && originalFile.parentFolders.length > 0) {
            originalFile.parentFolders.map((folder) => {
                if (state.folders[folder.id] && state.folders[folder.id].files) {
                    foldersToDelete.push({
                        ...state.folders[folder.id],
                        files: state.folders[folder.id].files ? [...state.folders[folder.id].files] : undefined
                    });
                }
            });
        }
        if (file.parentFolders && file.parentFolders.length > 0) {
            file.parentFolders.map((folder) => {
                if (state.folders[folder.id] && state.folders[folder.id].files) {
                    foldersToAdd.push({
                        ...state.folders[folder.id],
                        files: state.folders[folder.id].files ? [...state.folders[folder.id].files] : []
                    });
                }
            });
        }
        foldersToDelete = foldersToDelete.map((folder) => {
            folder.files = [...folder.files.filter((folderFile) => folderFile.id !== file.id)];
            return folder;
        });
        foldersToAdd = foldersToAdd.map((folder) => {
            folder.files = [...folder.files.filter((folderFile) => folderFile.id !== file.id)];
            folder.files = [...folder.files, file];
            return folder;
        });
        let updatedState = {
            ...newState,
            folders: {
                ...foldersToDelete.reduce(
                    (previous, folder) => ({ ...previous, [folder.id]: folder }),
                    newState.folders
                )
            }
        };
        updatedState = {
            ...updatedState,
            folders: {
                ...foldersToAdd.reduce(
                    (previous, folder) => ({ ...previous, [folder.id]: folder }),
                    updatedState.folders
                )
            }
        };
        return updatedState;
    }),
    on(documentActions.updateFile, (state) => ({
        ...state,
        editLoading: true
    })),
    on(documentActions.updatePreview, (state, { preview }) => ({
        ...state,
        toEdit: { ...state.toEdit, preview }
    })),
    on(documentActions.editFile, (state, { file }) => ({
        ...state,
        toEdit: { ...state.toEdit, file }
    })),
    on(documentActions.deleteFileFromFolderSuccess, (state, { file, folder }) => ({
        ...state,
        files: { ...state.files, [file.id]: file },
        folders: { ...state.folders, [folder.id]: folder }
    })),
    on(documentActions.getAllDrivesSuccess, (state, { drives }) => ({
        ...state,
        allDrives: drives
    }))
);

export function reducer(state: State | undefined, action: Action) {
    return documentReducer(state, action);
}
